import i18n from '@/i18n/config';
import { useDisclosure } from '@heroui/react';
import { toast } from 'react-toastify';
import { useState, useCallback } from 'react';
import { useAtom } from 'jotai';

import { createLeadInteraction, deleteLeadInteraction } from '@/store';
import type { Lead, LeadInteraction, LeadInteractionType } from '@/types';

export function useLeadInteractions() {
  const [, createInteraction] = useAtom(createLeadInteraction);
  const [, deleteInteraction] = useAtom(deleteLeadInteraction);
  const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onOpenChange: onAddModalOpenChange } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onOpenChange: onDeleteModalOpenChange } = useDisclosure();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [interactionToDelete, setInteractionToDelete] = useState<LeadInteraction | null>(null);

  const openAddModal = useCallback(
    (lead: Lead) => {
      setSelectedLead(lead);
      onAddModalOpen();
    },
    [onAddModalOpen]
  );

  const handleCreateInteraction = useCallback(
    async (type: LeadInteractionType, note: string, outcome?: string | null) => {
      if (!selectedLead) return;

      try {
        setIsSubmitting(true);
        await createInteraction({ leadId: selectedLead.id, type, note, outcome });
        toast.success(i18n.t('copy.interaction_added_successfully'));
        setIsSubmitting(false);
        onAddModalOpenChange();
        setSelectedLead(null);
      } catch (error) {
        setIsSubmitting(false);
        // Error is handled by axios interceptor
      }
    },
    [selectedLead, onAddModalOpenChange, createInteraction]
  );

  const openDeleteModal = useCallback(
    (lead: Lead, interaction: LeadInteraction) => {
      setSelectedLead(lead);
      setInteractionToDelete(interaction);
      onDeleteModalOpen();
    },
    [onDeleteModalOpen]
  );

  const handleDeleteInteraction = useCallback(async () => {
    if (!selectedLead || !interactionToDelete) return;

    try {
      setIsDeleting(true);
      await deleteInteraction({ leadId: selectedLead.id, interactionId: interactionToDelete.id });
      toast.success(i18n.t('copy.interaction_deleted_successfully'));
      setIsDeleting(false);
      setSelectedLead(null);
      setInteractionToDelete(null);
      onDeleteModalOpenChange();
    } catch (error) {
      setIsDeleting(false);
    }
  }, [selectedLead, interactionToDelete, deleteInteraction, onDeleteModalOpenChange]);

  return {
    isAddModalOpen,
    selectedLead,
    isSubmitting,
    isDeleting,
    openAddModal,
    handleCreateInteraction,
    handleDeleteInteraction,
    isDeleteModalOpen,
    openDeleteModal,
    interactionToDelete,
    onAddModalOpenChange,
    onDeleteModalOpenChange
  };
}

